import ErrorHandler from '@/exception/ErrorHandler';
import store from '@/store';
import axios from 'axios';
import { autoSpinner } from './spinner';
import { Utils } from './utils';
import { handle401Error, addPendingRequest, isHandling401 } from './authHandler';

// 영원히 resolve되지 않는 Promise (401/취소 시 후속 코드 실행 방지)
const PENDING_PROMISE = new Promise(() => {});

/**
 * Axios 인스턴스 생성
 * @param {string} serviceURL - 서비스 URL
 * @param {Object} options - 옵션
 * @param {Function} options.onResponse - 성공 콜백
 * @param {Function} options.onError - 에러 콜백
 * @param {boolean} options.showSpinner - 스피너 표시 여부 (기본값: true)
 * @param {string} serverURL - 서버 URL (선택)
 */
const getInstance = (
  serviceURL,
  { onResponse, onError, showSpinner = true },
  serverURL
) => {
  let server = serverURL ?? process.env.VUE_APP_URL_BASE_SERVER;

  const instance = axios.create({
    baseURL: server + serviceURL,
    headers: { 'Access-Control-Allow-Origin': '*' },
    timeout: 15000,
  });

  const authHeader = (req) => {
    const token = Utils.getSessionStorage('token');
    if (token) {
      req.headers.authorization = token;
    }
    return req.headers;
  };

  // 스피너 시작
  if (showSpinner) {
    autoSpinner.start();
  }

  // ------------ request ------------
  instance.interceptors.request.use(
    (req) => {
      // 요청 취소 토큰 설정
      const source = axios.CancelToken.source();
      req.cancelToken = source.token;
      addPendingRequest(source.cancel);

      req.headers = authHeader(req);
      return req;
    },
    (error) => {
      // 401 처리 중이면 에러 무시
      if (isHandling401()) {
        if (showSpinner) autoSpinner.stop();
        return PENDING_PROMISE;
      }
      if (onError) onError(error);
      if (showSpinner) autoSpinner.stop();
      return Promise.reject(error);
    }
  );

  // ------------ response ------------
  instance.interceptors.response.use(
    // ------- success -------
    (res) => {
      // 401 처리 중이면 응답 무시
      if (isHandling401()) {
        if (showSpinner) autoSpinner.stop();
        return PENDING_PROMISE;
      }

      const { data, headers } = res;

      if (data.statusCode === 200) {
        if (headers.authorization) {
          Utils.setSessionStorage('token', headers.authorization);
          store.dispatch('authority/setToken', headers.authorization);
        }
        onResponse(data.data);
        if (showSpinner) autoSpinner.stop();
        return data.data;
      }
      // ------- 401 in response body (토큰 만료) -------
      else if (data.statusCode === 401) {
        if (showSpinner) autoSpinner.stop();
        handle401Error(data.message || data.data?.message);
        return PENDING_PROMISE;
      }
      // ------- normal error (toast로 처리됨) -------
      else {
        ErrorHandler.common(data);
        if (onError) onError(res);
        if (showSpinner) autoSpinner.stop();
        return PENDING_PROMISE;
      }
    },
    // ------- network error -------
    (error) => {
      // 요청이 취소된 경우 (401로 인한 취소)
      if (axios.isCancel(error)) {
        if (showSpinner) autoSpinner.stop();
        return PENDING_PROMISE;
      }

      // 이미 401 처리 중이면 후속 코드 실행 불필요
      if (isHandling401()) {
        if (showSpinner) autoSpinner.stop();
        return PENDING_PROMISE;
      }

      const status = error.response?.status;

      // 401 Unauthorized - 토큰 만료, 재로그인 필요
      if (status === 401) {
        if (showSpinner) autoSpinner.stop();
        handle401Error(error.response?.data?.message);
        return PENDING_PROMISE;
      }

      // 403 Forbidden - 권한 없음 (toast로 처리됨)
      if (status === 403) {
        ErrorHandler.common(error.response?.data);
        if (onError) onError(error);
        if (showSpinner) autoSpinner.stop();
        return PENDING_PROMISE;
      }

      // 요청 타임아웃 (30초 초과)
      if (error.code === 'ECONNABORTED') {
        ErrorHandler.timeout();
        if (onError) onError(error);
        if (showSpinner) autoSpinner.stop();
        return PENDING_PROMISE;
      }

      // 기타 에러 (네트워크 에러 포함, toast로 처리됨)
      ErrorHandler.network();
      if (onError) onError(error);
      if (showSpinner) autoSpinner.stop();
      return PENDING_PROMISE;
    }
  );

  return instance;
};

export default getInstance;
